import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Shield, Users, Check, X, Plus, Trash2 } from 'lucide-react';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';

const UserRoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleUserCounts, setRoleUserCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [editedDisplayName, setEditedDisplayName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [newRoleData, setNewRoleData] = useState({
    name: '',
    display_name: '',
    permissions: {}
  });

  const permissionLabels = {
    create_user: 'Create Users',
    edit_user: 'Edit Users',
    create_project: 'Create Projects',
    edit_project: 'Edit Projects',
    create_floor: 'Create Floor Plans',
    edit_floor: 'Edit Floor Plans',
    create_ticket: 'Create Tickets',
    edit_ticket: 'Edit Tickets',
    approve_ticket: 'Approve/Reject Tickets',
    view_other_role_tickets: 'View Tickets from Other Roles' // <--- New
  };

  const roleDescriptions = {
    admin: 'Full system access with all permissions',
    project_manager: 'Can manage projects, floors, and approve tickets',
    inspector: 'Can create and manage tickets on assigned projects',
    repair_engineer: 'Can view and add comments to assigned tickets'
  };

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      setRoles(response.data);
    } catch (error) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      const usersList = response.data;
      setUsers(usersList);
      
      // Count users per role
      const counts = {};
      usersList.forEach(user => {
        counts[user.role] = (counts[user.role] || 0) + 1;
      });
      
      console.log('Role user counts:', counts);
      console.log('Users list:', usersList.map(u => ({ name: u.name, role: u.role })));
      
      setRoleUserCounts(counts);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setPermissions(role.permissions || {});
    setEditedDisplayName(role.display_name || '');
    setDialogOpen(true);
  };

  const togglePermission = (key) => {
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      // Update both permissions and display name
      await axiosInstance.put(`/roles/${selectedRole.id}`, {
        permissions: permissions,
        display_name: editedDisplayName
      });
      
      toast.success('Role updated successfully!');
      await fetchRoles();
      setDialogOpen(false);
      setSelectedRole(null);
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleData.name || !newRoleData.display_name) {
      toast.error('Please provide role name and display name');
      return;
    }

    // Validate role name format (lowercase with underscores)
    const roleNameRegex = /^[a-z_]+$/;
    if (!roleNameRegex.test(newRoleData.name)) {
      toast.error('Role name must be lowercase letters and underscores only (e.g., site_manager)');
      return;
    }

    try {
      console.log('Creating role with data:', newRoleData);
      const response = await axiosInstance.post('/roles', newRoleData);
      console.log('Role created successfully:', response.data);
      
      toast.success('Role created successfully!');
      await fetchRoles();
      await fetchUsers(); // Refresh user counts
      setCreateDialogOpen(false);
      setNewRoleData({ name: '', display_name: '', permissions: {} });
    } catch (error) {
      console.error('Failed to create role:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create role');
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      await axiosInstance.delete(`/roles/${roleToDelete.id}`);
      toast.success('Role deleted successfully!');
      await fetchRoles();
      await fetchUsers(); // Refresh user counts
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const openDeleteDialog = (role, e) => {
    e.stopPropagation();
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const isSystemRole = (roleName) => {
    // Only admin role cannot be deleted
    return roleName === 'admin';
  };

  const countPermissions = (rolePermissions) => {
    if (!rolePermissions) return 0;
    return Object.values(rolePermissions).filter(Boolean).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Role & Permission Management</h1>
          <p className="text-muted-foreground mt-2">Configure permissions for each role - all users with a role inherit these permissions</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {roles.map((role) => {
          const userCount = roleUserCounts[role.name] || 0;
          console.log(`Role: ${role.name}, Display: ${role.display_name}, User count: ${userCount}`);
          
          return (
            <Card
              key={role.id}
              className="hover:border-primary cursor-pointer transition-colors"
              onClick={() => handleRoleClick(role)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl capitalize">{role.display_name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {roleDescriptions[role.name] || 'Custom role'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-lg px-3 py-1">
                      {countPermissions(role.permissions)}/9
                    </Badge>
                    <Badge variant="outline" className="text-sm">
                      {userCount} users
                    </Badge>
                  {!isSystemRole(role.name) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`hover:bg-destructive/10 ${
                        userCount > 0 
                          ? 'text-gray-400 cursor-not-allowed opacity-50' 
                          : 'text-destructive hover:text-destructive'
                      }`}
                      onClick={(e) => {
                        if (userCount === 0) {
                          openDeleteDialog(role, e);
                        }
                      }}
                      disabled={userCount > 0}
                      title={userCount > 0 ? `Cannot delete: ${userCount} user(s) assigned` : 'Delete role'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Permission Grid */}
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(permissionLabels).map(([key, label]) => {
                  const hasPermission = role.permissions?.[key];
                  return (
                    <div key={key} className="flex flex-col items-center p-3 border rounded-lg">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                        hasPermission ? 'bg-green-500' : 'bg-muted'
                      }`}>
                        {hasPermission ? (
                          <Check className="w-5 h-5 text-white" />
                        ) : (
                          <X className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <p className="text-xs text-center text-muted-foreground leading-tight">
                        {label.split(' ')[0]}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
        })}
      </div>

      {/* Permission Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Edit {selectedRole?.display_name}
            </DialogTitle>
            <DialogDescription>
              Update display name and permissions for this role
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Display Name Input */}
            <div className="space-y-2">
              <Label htmlFor="edit-display-name">Display Name</Label>
              <Input
                id="edit-display-name"
                value={editedDisplayName}
                onChange={(e) => setEditedDisplayName(e.target.value)}
                placeholder="e.g., Site Manager"
              />
              <p className="text-xs text-muted-foreground">User-friendly name shown throughout the application</p>
            </div>
            {/* User Management */}
            <div className="space-y-3">
              <h3 className="font-semibold text-base flex items-center gap-2 pb-2 border-b">
                <Users className="w-4 h-4" />
                User Management
              </h3>
              <div className="space-y-2 pl-2">
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="create_user" className="cursor-pointer font-medium">
                      {permissionLabels.create_user}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow creating new user accounts</p>
                  </div>
                  <Switch
                    id="create_user"
                    checked={permissions.create_user || false}
                    onCheckedChange={() => togglePermission('create_user')}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="edit_user" className="cursor-pointer font-medium">
                      {permissionLabels.edit_user}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow editing user details and roles</p>
                  </div>
                  <Switch
                    id="edit_user"
                    checked={permissions.edit_user || false}
                    onCheckedChange={() => togglePermission('edit_user')}
                  />
                </div>
              </div>
            </div>

            {/* Project Management */}
            <div className="space-y-3">
              <h3 className="font-semibold text-base flex items-center gap-2 pb-2 border-b">
                <Shield className="w-4 h-4" />
                Project Management
              </h3>
              <div className="space-y-2 pl-2">
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="create_project" className="cursor-pointer font-medium">
                      {permissionLabels.create_project}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow creating new projects</p>
                  </div>
                  <Switch
                    id="create_project"
                    checked={permissions.create_project || false}
                    onCheckedChange={() => togglePermission('create_project')}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="edit_project" className="cursor-pointer font-medium">
                      {permissionLabels.edit_project}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow editing project details</p>
                  </div>
                  <Switch
                    id="edit_project"
                    checked={permissions.edit_project || false}
                    onCheckedChange={() => togglePermission('edit_project')}
                  />
                </div>
              </div>
            </div>

            {/* Floor Plan Management */}
            <div className="space-y-3">
              <h3 className="font-semibold text-base flex items-center gap-2 pb-2 border-b">
                <Shield className="w-4 h-4" />
                Floor Plan Management
              </h3>
              <div className="space-y-2 pl-2">
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="create_floor" className="cursor-pointer font-medium">
                      {permissionLabels.create_floor}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow uploading floor plans</p>
                  </div>
                  <Switch
                    id="create_floor"
                    checked={permissions.create_floor || false}
                    onCheckedChange={() => togglePermission('create_floor')}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="edit_floor" className="cursor-pointer font-medium">
                      {permissionLabels.edit_floor}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow editing and deleting floor plans</p>
                  </div>
                  <Switch
                    id="edit_floor"
                    checked={permissions.edit_floor || false}
                    onCheckedChange={() => togglePermission('edit_floor')}
                  />
                </div>
              </div>
            </div>

            {/* Ticket Management */}
            <div className="space-y-3">
              <h3 className="font-semibold text-base flex items-center gap-2 pb-2 border-b">
                <Shield className="w-4 h-4" />
                Ticket Management
              </h3>
              <div className="space-y-2 pl-2">
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="create_ticket" className="cursor-pointer font-medium">
                      {permissionLabels.create_ticket}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow creating new tickets</p>
                  </div>
                  <Switch
                    id="create_ticket"
                    checked={permissions.create_ticket || false}
                    onCheckedChange={() => togglePermission('create_ticket')}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="edit_ticket" className="cursor-pointer font-medium">
                      {permissionLabels.edit_ticket}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow editing ticket details</p>
                  </div>
                  <Switch
                    id="edit_ticket"
                    checked={permissions.edit_ticket || false}
                    onCheckedChange={() => togglePermission('edit_ticket')}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="approve_ticket" className="cursor-pointer font-medium">
                      {permissionLabels.approve_ticket}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">Allow approving or rejecting ticket fixes</p>
                  </div>
                  <Switch
                    id="approve_ticket"
                    checked={permissions.approve_ticket || false}
                    onCheckedChange={() => togglePermission('approve_ticket')}
                  />
                </div>

                {/* Nút View Other Role */}
                <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <Label htmlFor="view_other_role_tickets" className="cursor-pointer font-medium">
                      {permissionLabels.view_other_role_tickets}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Allow viewing tickets created by users in DIFFERENT roles.
                    </p>
                  </div>
                  <Switch
                    id="view_other_role_tickets"
                    checked={permissions.view_other_role_tickets || false}
                    onCheckedChange={() => togglePermission('view_other_role_tickets')}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setSelectedRole(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSavePermissions}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
            <DialogDescription>
              Create a custom role with specific permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">Role Name</Label>
              <Input
                id="role-name"
                placeholder="e.g., contractor"
                value={newRoleData.name}
                onChange={(e) => setNewRoleData({ ...newRoleData, name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Use lowercase with underscores (e.g., site_manager)</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="display-name">Display Name</Label>
              <Input
                id="display-name"
                placeholder="e.g., Contractor"
                value={newRoleData.display_name}
                onChange={(e) => setNewRoleData({ ...newRoleData, display_name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">User-friendly name shown in the UI</p>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setNewRoleData({ name: '', display_name: '', permissions: {} });
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateRole}>
              Create Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Role Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the role "{roleToDelete?.display_name}"? 
              This action cannot be undone. Users with this role will need to be assigned a different role.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRoleToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRole} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserRoleManagement;
